import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";

export type RetakeDraftItem = {
    id: string;
    startSec: number;
    endSec: number;
    confidence: number;
    removedText: string;
    keptText: string;
    reason?: string;
};

type RemoveRetakesSidebarProps = {
    onBack: () => void;
    onDetect?: () => void;
    onApply?: () => void;
    isLoading?: boolean;
    hasRun?: boolean;
    error?: string | null;
    draft?: RetakeDraftItem[];
};

const formatMMSS = (seconds: number): string => {
    const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const truncate = (value: string, maxChars: number): string => {
    const cleaned = String(value ?? "").replace(/\s+/g, " ").trim();
    if (!cleaned) return "";
    if (cleaned.length <= maxChars) return cleaned;
    return `${cleaned.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`;
};

const pct = (value: number): string => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0%";
    return `${Math.round(Math.max(0, Math.min(1, n)) * 100)}%`;
};

const RemoveRetakesSidebar: React.FC<RemoveRetakesSidebarProps> = ({
    onBack,
    onDetect,
    onApply,
    isLoading,
    hasRun,
    error,
    draft,
}) => {
    const hasDraft = Array.isArray(draft) && draft.length > 0;
    const detectButtonLabel = hasDraft ? "Redetect" : "Detect retakes";

    const rows = useMemo(() => {
        if (!hasDraft) return [];
        return (draft ?? []).map((d, idx) => ({
            key: `${idx}-${d.id}`,
            start: formatMMSS(Number(d.startSec ?? 0)),
            end: formatMMSS(Number(d.endSec ?? 0)),
            confidence: pct(Number(d.confidence ?? 0)),
            removedText: truncate(String(d.removedText ?? ""), 130),
            keptText: truncate(String(d.keptText ?? ""), 130),
            reason: truncate(String(d.reason ?? ""), 110),
        }));
    }, [draft, hasDraft]);

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                p: "3px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "8px" }}>
                <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
                    <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
                    Remove retakes
                </Typography>
            </Box>

            <Box
                sx={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                }}
            >
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                    Detect obvious repeated takes conservatively, then apply red strikethrough.
                </Typography>

                <Button
                    fullWidth
                    onClick={onDetect}
                    disabled={Boolean(isLoading) || !onDetect}
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        textTransform: "none",
                        color: "#FFFFFF",
                        backgroundColor: "#1A1A1A",
                        borderRadius: "8px",
                        height: "40px",
                        "&:hover": {
                            backgroundColor: "#2B2B2B",
                        },
                        "&:disabled": {
                            backgroundColor: "#E5E7EB",
                            color: "#9CA3AF",
                        },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        {isLoading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
                        <Box>{detectButtonLabel}</Box>
                    </Box>
                </Button>

                {!!error && (
                    <Box
                        sx={{
                            backgroundColor: "#FEF2F2",
                            border: "1px solid #FECACA",
                            borderRadius: "10px",
                            px: "10px",
                            py: "8px",
                        }}
                    >
                        <Typography sx={{ fontSize: 12.5, color: "#991B1B" }}>{error}</Typography>
                    </Box>
                )}

                {!error && !isLoading && hasRun && !hasDraft ? (
                    <Box
                        sx={{
                            backgroundColor: "#F9FAFB",
                            border: "1px solid #E5E7EB",
                            borderRadius: "10px",
                            px: "10px",
                            py: "8px",
                        }}
                    >
                        <Typography sx={{ fontSize: 12.5, color: "#374151" }}>
                            No obvious retakes detected. Detection stays conservative by default.
                        </Typography>
                    </Box>
                ) : null}
            </Box>

            {hasDraft ? (
                <Box
                    sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: "12px",
                        padding: "12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>Preview</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
                            {rows.length} {rows.length === 1 ? "retake" : "retakes"}
                        </Typography>
                    </Box>
                    <Divider sx={{ borderColor: "#E5E7EB" }} />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflow: "auto" }}>
                        {rows.map((row) => (
                            <Box
                                key={row.key}
                                sx={{
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "10px",
                                    px: "10px",
                                    py: "9px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 12.5,
                                            color: "#6B7280",
                                            flexShrink: 0,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {row.start} - {row.end}
                                    </Typography>
                                    <Box
                                        sx={{
                                            marginLeft: "auto",
                                            px: "7px",
                                            py: "1px",
                                            borderRadius: "999px",
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            color: "#92400E",
                                            backgroundColor: "#FEF3C7",
                                            border: "1px solid #FDE68A",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {row.confidence}
                                    </Box>
                                </Box>
                                <Typography sx={{ fontSize: 12.5, color: "#991B1B", lineHeight: 1.45 }}>
                                    Remove: {row.removedText || "(no text)"}
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: "#065F46", lineHeight: 1.45 }}>
                                    Keep: {row.keptText || "(no text)"}
                                </Typography>
                                {row.reason ? (
                                    <Typography sx={{ fontSize: 12, color: "#6B7280", lineHeight: 1.4 }}>
                                        {row.reason}
                                    </Typography>
                                ) : null}
                            </Box>
                        ))}
                    </Box>

                    <Button
                        fullWidth
                        onClick={onApply}
                        disabled={Boolean(isLoading) || !onApply}
                        sx={{
                            mt: "2px",
                            fontFamily: "Inter",
                            fontSize: "14px",
                            fontWeight: 500,
                            textTransform: "none",
                            color: "#FFFFFF",
                            backgroundColor: "#1A1A1A",
                            borderRadius: "8px",
                            height: "40px",
                            "&:hover": {
                                backgroundColor: "#2B2B2B",
                            },
                            "&:disabled": {
                                backgroundColor: "#E5E7EB",
                                color: "#9CA3AF",
                            },
                        }}
                    >
                        Apply red strikethrough
                    </Button>
                </Box>
            ) : null}
        </Box>
    );
};

export default RemoveRetakesSidebar;
